import React, { memo } from 'react';

import { Card, CardContent, CardHeader, Divider, Typography } from '@mui/material';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { mediaQuery } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

const defaultObject = {};

const getCardStyles = ({ disableBorderRadius, fitContent, contentFullBleed }) => ({
  backgroundColor: ({ palette }) => palette.background.primary,
  boxShadow: ({ palette }) => palette.shadow.s,
  // contentFullBleed: drop the panel padding so an edge-to-edge child (e.g. the Pipeline Zero
  // celebration photo) reaches the card's rounded border; the card keeps its overflow:hidden +
  // borderRadius so the child still clips to the rounded corners.
  padding: contentFullBleed ? 0 : '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',

  [mediaQuery.mobile]: {
    padding: contentFullBleed ? 0 : '8px',
  },

  // fitContent: don't let the card shrink/clip — size to content so the page
  // scroller (ContentLayout `main`) scrolls the whole page. (MUI Card defaults
  // to overflow:hidden, which would otherwise clip the grown content.)
  ...getConditionalObject(fitContent, {
    flexShrink: 0,
    overflow: 'visible',
  }),

  ...getConditionalObject(!disableBorderRadius, {
    borderRadius: ({ shape }) => shape.radius.l,
  }),
});

const cardHeaderStyles = {
  padding: '4px',

  '& 	.MuiCardHeader-action': {
    margin: 0,
  },
};

const getContentOverflowStyles = ({ fitContent, contentFullBleed }) => {
  // fitContent: size to the content so the page-level scroller (ContentLayout `main`) scrolls the
  // whole page instead of the card scrolling internally.
  if (fitContent) {
    return { flex: '0 0 auto', overflow: 'visible' };
  }

  // contentFullBleed: fill the card and let the child reach the card's edge; the card (not this
  // content box) owns the overflow:hidden + rounded clip, so overflow stays visible here.
  if (contentFullBleed) {
    return { flex: 1, overflow: 'visible' };
  }

  // Default: fill the card and clip overflow (the card owns its scroll region).
  return { flex: 1, overflow: 'hidden' };
};

const getCardContentStyles = ({ fitContent, contentFullBleed }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: contentFullBleed ? 0 : '4px',

  ...getContentOverflowStyles({ fitContent, contentFullBleed }),

  '&:last-of-type': {
    paddingBottom: contentFullBleed ? 0 : '4px',
  },
});

const PageBlock = ({
  title,
  withDivider = false,
  children,
  withAction = false,
  isActionDisabled = false,
  onMenuClick,
  isMenuOpened = false,
  actionContent,
  classes = defaultObject,
  isFullHeight = false,
  fitContent = false,
  disableBorderRadius = false,
  contentFullBleed = false,
  dataTestId,
}) => (
  <Card
    className={clsx(
      {
        'h-full': isFullHeight,
      },
      classes.wrapper,
    )}
    sx={getCardStyles({ disableBorderRadius, fitContent, contentFullBleed })}
  >
    {(title || withAction) && (
      <>
        <CardHeader
          action={
            withAction &&
            (actionContent ? (
              actionContent
            ) : (
              <MoreButtonCDS
                aria-label="settings"
                disabled={isActionDisabled}
                isSelected={isMenuOpened}
                onClick={onMenuClick}
              />
            ))
          }
          className={classes.header}
          sx={cardHeaderStyles}
          title={
            <Typography data-testid={dataTestId} variant="medium20">
              {title}
            </Typography>
          }
        />

        {withDivider && (
          <div className="m-2">
            <Divider />
          </div>
        )}
      </>
    )}

    <CardContent
      className={classes.content}
      sx={getCardContentStyles({ fitContent, contentFullBleed })}
    >
      {children}
    </CardContent>
  </Card>
);

PageBlock.propTypes = {
  isActionDisabled: PropTypes.bool,
  isMenuOpened: PropTypes.bool,
  title: PropTypes.node,
  withAction: PropTypes.bool,
  withDivider: PropTypes.bool,
  actionContent: PropTypes.node,
  classes: PropTypes.object,
  isFullHeight: PropTypes.bool,
  fitContent: PropTypes.bool,
  disableBorderRadius: PropTypes.bool,
  contentFullBleed: PropTypes.bool,
  dataTestId: PropTypes.string,
  onMenuClick: PropTypes.func,
};

export default memo(PageBlock);
